// orchestrator_fullstack/frontend/src/app/admin/oidc/TenantsOidcPage.tsx
import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "../../auth/AuthProvider";
import DangerConfirmModal from "../../components/DangerConfirmModal";
import {
  listGroupRoleMappings,
  listGroupTenantMappings,
  upsertGroupRoleMapping,
  upsertGroupTenantMapping,
  deleteGroupTenantMapping,
  deleteGroupRoleMapping,
  type Role,
} from "./api";
import { apiFetch } from "../../auth/api";

const ROLES: Role[] = ["viewer", "operator", "admin", "platform-admin"];

// /v1/admin/tenants now returns:
//   { tenant_id: <uuid>, tenant_slug: <slug>, name, active, created_at, updated_at }
type TenantRow = {
  tenant_id: string;         // UUID
  tenant_slug?: string | null; // slug
  name?: string | null;
  active?: boolean | null;
  created_at?: string | null;
  updated_at?: string | null;
};

function tenantUuid(t: TenantRow): string {
  return String(t.tenant_id || "").trim();
}

function tenantSlug(t: TenantRow): string {
  return String(t.tenant_slug || "").trim();
}

function tenantLabel(t: TenantRow): string {
  const nm = String(t.name || "").trim();
  const slug = tenantSlug(t);
  if (nm && slug && nm !== slug) return `${nm} (${slug})`;
  return nm || slug || tenantUuid(t);
}

export default function TenantsOidcPage() {
  const { state } = useAuth();

  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const [tenants, setTenants] = useState<TenantRow[]>([]);
  // Selected tenant UUID (NOT slug)
  const [tenantSelUuid, setTenantSelUuid] = useState<string>("");

  const [groupTenantRows, setGroupTenantRows] = useState<any[]>([]);
  const [groupRoleRows, setGroupRoleRows] = useState<any[]>([]);

  const [groupName, setGroupName] = useState("");
  const [groupTenantTargetUuid, setGroupTenantTargetUuid] = useState("");

  const [groupRoleName, setGroupRoleName] = useState("");
  const [groupRole, setGroupRole] = useState<Role>("viewer");

  const [confirm, setConfirm] = useState<
    null | { label: string; confirmWord: string; onYes: () => Promise<void> }
  >(null);

  // Persist selected tenant across reloads
  const TENANT_SEL_STORAGE_KEY = "orchestrator.admin.oidc.selectedTenantUuid";

  // Prevent stale async responses from overwriting UI state
  const groupRoleReqRef = React.useRef(0);
  const groupRoleAbortRef = React.useRef<AbortController | null>(null);

  async function refreshPlatformScoped() {
    setErr(null);
    try {
      const ts = await apiFetch<TenantRow[]>(`/v1/admin/tenants`, { admin: true });
      const list = (ts || []).filter((t) => tenantUuid(t));
      setTenants(list);

      // Default selection:
      // 1) keep current selection if already set
      // 2) else prefer localStorage selection if still valid
      // 3) else prefer root tenant if present
      // 4) else fall back to first tenant
      const root = list.find((t) => tenantSlug(t) === "root");
      const rootUuid = tenantUuid(root || ({} as any));
      const firstUuid = tenantUuid(list[0] || ({} as any));

      const stored = (() => {
        try {
          return String(localStorage.getItem(TENANT_SEL_STORAGE_KEY) || "").trim();
        } catch {
          return "";
        }
      })();

      const storedValid = stored && list.some((t) => tenantUuid(t) === stored);
      setTenantSelUuid((prev) => {
        if (prev && list.some((t) => tenantUuid(t) === prev)) return prev;
        if (storedValid) return stored;
        return rootUuid || firstUuid || "";
      });

      const gt = await listGroupTenantMappings();
      setGroupTenantRows(gt || []);
    } catch (e: any) {
      setErr(e?.message || String(e));
    }
  }

  async function refreshTenantScoped(tenantUuidValue: string) {
    // bump request id (latest wins)
    const reqId = ++groupRoleReqRef.current;

    // cancel any in-flight request (best-effort)
    try {
      groupRoleAbortRef.current?.abort();
    } catch {
      // ignore
    }
    const ac = new AbortController();
    groupRoleAbortRef.current = ac;

    setErr(null);
    try {
      if (!tenantUuidValue) {
        setGroupRoleRows([]);
        return;
      }

      // If your api.ts currently doesn't accept signal, this still works:
      // - abort helps if apiFetch forwards signal
      // - reqId check guarantees correctness either way
      const gr = await (listGroupRoleMappings as any)(tenantUuidValue, { signal: ac.signal });

      // Only apply if this is the latest request
      if (reqId !== groupRoleReqRef.current) return;

      setGroupRoleRows(gr || []);
    } catch (e: any) {
      // Ignore abort errors
      if (e?.name === "AbortError") return;
      if (reqId !== groupRoleReqRef.current) return;
      setErr(e?.message || String(e));
      setGroupRoleRows([]);
    }
  }

  useEffect(() => {
    refreshPlatformScoped();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (tenantSelUuid) refreshTenantScoped(tenantSelUuid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantSelUuid]);

  useEffect(() => {
    if (!tenantSelUuid) return;
    try {
      localStorage.setItem(TENANT_SEL_STORAGE_KEY, tenantSelUuid);
    } catch {
      // ignore
    }
  }, [tenantSelUuid]);

  const tenantOptions = useMemo(
    () =>
      tenants
        .map((t) => ({ id: tenantUuid(t), label: tenantLabel(t) }))
        .filter((x) => x.id),
    [tenants]
  );

  const selectedTenant = useMemo(
    () => tenants.find((t) => tenantUuid(t) === tenantSelUuid) || null,
    [tenants, tenantSelUuid]
  );

  return (
    <div className="p-6 max-w-[1200px]">
      <div className="text-2xl font-extrabold">Tenants & OIDC</div>
      <div className="text-sm text-zinc-600 mt-1">
        Platform-wide mappings. You:{" "}
        <span className="font-mono">{state.user || "(unknown)"}</span> · Roles:{" "}
        <span className="font-mono">{state.roles.join(", ") || "(none)"}</span>
      </div>

      {err && (
        <div className="mt-4 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          {err}
        </div>
      )}

      {/* Tenants list */}
      <div className="mt-6 rounded-2xl border border-zinc-200 bg-white p-4">
        <div className="flex items-center justify-between">
          <div className="font-bold">Tenants</div>
          <div className="text-xs text-zinc-500">{tenants.length} tenants</div>
        </div>

        <div className="mt-3 flex items-center gap-2">
          <div className="text-xs font-semibold text-zinc-600">Tenant</div>
          <select
            className="h-10 rounded-xl border border-zinc-200 px-3 text-sm"
            value={tenantSelUuid}
            onChange={(e) => setTenantSelUuid(e.target.value)}
          >
            {tenantOptions.map((t) => (
              <option key={t.id} value={t.id}>
                {t.label}
              </option>
            ))}
          </select>
        </div>

        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-zinc-500">
                <th className="py-2">tenant_id (uuid)</th>
                <th className="py-2">tenant_slug</th>
                <th className="py-2">name</th>
                <th className="py-2">active</th>
              </tr>
            </thead>
            <tbody>
              {tenants.map((t) => {
                const id = tenantUuid(t);
                return (
                  <tr key={id} className="border-t border-zinc-100">
                    <td className="py-2 font-mono text-xs">{id}</td>
                    <td className="py-2 font-mono text-xs">{tenantSlug(t) || "-"}</td>
                    <td className="py-2">{t.name ?? tenantLabel(t)}</td>
                    <td className="py-2 text-xs text-zinc-700">{String(t.active ?? "")}</td>
                  </tr>
                );
              })}
              {tenants.length === 0 && (
                <tr>
                  <td colSpan={4} className="py-4 text-xs text-zinc-500">
                    No tenants found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Group -> Tenant mapping */}
      <div className="mt-6 rounded-2xl border border-zinc-200 bg-white p-4">
        <div className="font-bold">Group → Tenant</div>

        <div className="mt-3 flex flex-col md:flex-row gap-2">
          <input
            className="h-10 flex-1 rounded-xl border border-zinc-200 px-3 text-sm"
            placeholder="Group name"
            value={groupName}
            onChange={(e) => setGroupName(e.target.value)}
          />
          <select
            className="h-10 rounded-xl border border-zinc-200 px-3 text-sm"
            value={groupTenantTargetUuid}
            onChange={(e) => setGroupTenantTargetUuid(e.target.value)}
          >
            <option value="">Select tenant…</option>
            {tenantOptions.map((t) => (
              <option key={t.id} value={t.id}>
                {t.label}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="h-10 rounded-xl bg-black px-4 text-sm font-bold text-white disabled:opacity-50"
            disabled={busy || !groupName.trim() || !groupTenantTargetUuid}
            onClick={async () => {
              setBusy(true);
              setErr(null);
              try {
                await upsertGroupTenantMapping(groupName.trim(), groupTenantTargetUuid);
                setGroupName("");
                setGroupTenantTargetUuid("");
                await refreshPlatformScoped();
              } catch (e: any) {
                setErr(e?.message || String(e));
              } finally {
                setBusy(false);
              }
            }}
          >
            Add / Update
          </button>
        </div>

        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-zinc-500">
                <th className="py-2">group_name</th>
                <th className="py-2">tenant_id (uuid)</th>
                <th className="py-2"></th>
              </tr>
            </thead>
            <tbody>
              {groupTenantRows.map((r) => (
                <tr key={r.group_name} className="border-t border-zinc-100">
                  <td className="py-2 font-mono">{r.group_name}</td>
                  <td className="py-2 font-mono text-xs">{r.tenant_id}</td>
                  <td className="py-2 text-right">
                    <button
                      className="rounded-lg border border-zinc-200 px-3 py-1 text-xs font-semibold hover:bg-zinc-50"
                      onClick={() =>
                        setConfirm({
                          label: `Remove group→tenant mapping for ${r.group_name}`,
                          confirmWord: "REMOVE",
                          onYes: async () => {
                            await deleteGroupTenantMapping(r.group_name);
                            await refreshPlatformScoped();
                          },
                        })
                      }
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
              {groupTenantRows.length === 0 && (
                <tr>
                  <td colSpan={3} className="py-4 text-xs text-zinc-500">
                    No group→tenant mappings.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Group -> Role mapping */}
      <div className="mt-6 rounded-2xl border border-zinc-200 bg-white p-4">
        <div className="font-bold">Group → Role (per tenant)</div>
        <div className="text-xs text-zinc-500 mt-1">
          Selected tenant UUID: <span className="font-mono">{tenantSelUuid || "(none)"}</span>
          {selectedTenant ? (
            <>
              {" "}
              · <span className="font-mono">{tenantLabel(selectedTenant)}</span>
            </>
          ) : null}
        </div>

        <div className="mt-3 flex flex-col md:flex-row gap-2">
          <input
            className="h-10 flex-1 rounded-xl border border-zinc-200 px-3 text-sm"
            placeholder="Group name"
            value={groupRoleName}
            onChange={(e) => setGroupRoleName(e.target.value)}
          />
          <select
            className="h-10 rounded-xl border border-zinc-200 px-3 text-sm"
            value={groupRole}
            onChange={(e) => setGroupRole(e.target.value as Role)}
          >
            {ROLES.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
          <button
            className="h-10 rounded-xl bg-black px-4 text-sm font-bold text-white disabled:opacity-50"
            disabled={busy || !tenantSelUuid || !groupRoleName.trim()}
            onClick={async () => {
              setBusy(true);
              setErr(null);
              try {
                await upsertGroupRoleMapping(tenantSelUuid, groupRoleName.trim(), groupRole);
                setGroupRoleName("");
                await refreshTenantScoped(tenantSelUuid);
              } catch (e: any) {
                setErr(e?.message || String(e));
              } finally {
                setBusy(false);
              }
            }}
          >
            Add / Update
          </button>
        </div>

        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-zinc-500">
                <th className="py-2">group_name</th>
                <th className="py-2">role</th>
                <th className="py-2"></th>
              </tr>
            </thead>
            <tbody>
              {groupRoleRows.map((r) => (
                <tr key={`${r.group_name}-${r.role}`} className="border-t border-zinc-100">
                  <td className="py-2 font-mono">{r.group_name}</td>
                  <td className="py-2">{r.role}</td>
                  <td className="py-2 text-right">
                    <button
                      className="rounded-lg border border-zinc-200 px-3 py-1 text-xs font-semibold hover:bg-zinc-50"
                      onClick={() =>
                        setConfirm({
                          label: `Remove group→role mapping ${r.group_name} → ${r.role}`,
                          confirmWord: "REMOVE",
                          onYes: async () => {
                            await deleteGroupRoleMapping(tenantSelUuid, r.group_name, r.role);
                            await refreshTenantScoped(tenantSelUuid);
                          },
                        })
                      }
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
              {groupRoleRows.length === 0 && (
                <tr>
                  <td colSpan={3} className="py-4 text-xs text-zinc-500">
                    No group→role mappings for this tenant.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <DangerConfirmModal
        open={!!confirm}
        title="Confirm removal"
        description={confirm?.label || ""}
        confirmKeyword={confirm?.confirmWord || "REMOVE"}
        confirmLabel="Remove"
        requireReason={false}
        onCancel={() => setConfirm(null)}
        onConfirm={async () => {
          if (!confirm) return;
          setBusy(true);
          setErr(null);
          try {
            await confirm.onYes();
            setConfirm(null);
          } catch (e: any) {
            setErr(e?.message || String(e));
          } finally {
            setBusy(false);
          }
        }}
      />
    </div>
  );
}